import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Download, Search, X } from "lucide-react";
import FooterNew from "../components/FooterNew";
import { companyProjects, allProjects, androidApps, categories, metrics } from "../data/mockData";

const Projects = () => {
  const [activeFilter, setActiveFilter] = useState("All Projects");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedAndroidApp, setSelectedAndroidApp] = useState(null);

  useEffect(() => {
    document.body.style.overflow = selectedAndroidApp ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [selectedAndroidApp]);

  const filtered = allProjects.filter((p) => {
    const matchCategory = activeFilter === "All Projects" || p.category === activeFilter;
    const matchSearch = p.title.toLowerCase().includes(searchTerm.toLowerCase()) || p.desc.toLowerCase().includes(searchTerm.toLowerCase());
    return matchCategory && matchSearch;
  });

  return (
    <main className="bg-primary">
      {/* Hero */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/3 left-1/3 w-96 h-96 rounded-full opacity-15 blur-3xl" style={{ background: "radial-gradient(circle, #27B8FF, transparent)" }} />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <span className="inline-block px-4 py-1.5 rounded-full bg-accent/10 border border-accent/20 text-ai-glow text-sm font-medium mb-6">Case Studies</span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white mb-6">
              AI Projects & <span className="gradient-text">Case Studies</span>
            </h1>
            <p className="text-xl text-slate-400">Real-World AI Solutions Driving Business Transformation</p>
          </motion.div>
        </div>
      </section>

      {/* Company Projects */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-14">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15, duration: 0.6 }}>
          <div className="text-center mb-10">
            <span className="inline-block px-4 py-1.5 rounded-full bg-accent/10 border border-accent/20 text-ai-glow text-sm font-medium mb-4">
              Company Projects
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-3">
              Websites We Built for <span className="gradient-text">Our Clients</span>
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">Explore some of our delivered company projects with direct live links.</p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {companyProjects.map((project, i) => (
              <motion.a
                key={project.title}
                href={project.url}
                target="_blank"
                rel="noreferrer"
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 + i * 0.08, duration: 0.45 }}
                whileHover={{ y: -6 }}
                className="group rounded-2xl overflow-hidden w-full max-w-sm mx-auto"
                style={{ background: "rgba(10, 16, 41, 0.45)", border: "1px solid rgba(124, 77, 255, 0.15)" }}
              >
                <div className="h-44 overflow-hidden">
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    loading="lazy"
                  />
                </div>
                <div className="p-4">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <h3 className="text-base font-semibold text-white group-hover:text-ai-glow transition-colors">{project.title}</h3>
                    <ArrowUpRight className="w-4 h-4 text-slate-400 group-hover:text-ai-glow shrink-0 mt-0.5" />
                  </div>
                  <p className="text-sm text-slate-400 leading-relaxed">{project.desc}</p>
                </div>
              </motion.a>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Android App Projects */}
      <section className="relative py-14 overflow-hidden">
        {/* Background Decorative Elements */}
        <div className="absolute inset-0 tech-grid opacity-10 pointer-events-none" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div 
            initial={{ opacity: 0, y: 24 }} 
            animate={{ opacity: 1, y: 0 }} 
            transition={{ delay: 0.18, duration: 0.6 }}
            className="text-center mb-10"
          >
            <p className="text-sm text-slate-500 mb-2">Showing {androidApps.length} project(s)</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-3">
              Mobile Apps We Built for <span className="gradient-text">Our Clients</span>
            </h2>
          </motion.div>

          <div className="grid lg:grid-cols-3 gap-8">
            {androidApps.map((app, i) => (
              <motion.div
                key={app.title}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.22 + i * 0.1, duration: 0.5 }}
                className="group rounded-[2rem] p-6 flex flex-col h-full"
                style={{ 
                  background: "rgba(8, 12, 28, 0.7)", 
                  backdropFilter: "blur(12px)",
                  border: "1px solid rgba(124, 77, 255, 0.15)",
                  boxShadow: "0 20px 50px rgba(0, 0, 0, 0.3)"
                }}
              >
                {/* Image Gallery */}
                <div className="relative mb-8 pt-4">
                  <div className="flex gap-4 overflow-x-auto pb-4 px-2 scrollbar-hide snap-x snap-mandatory">
                    {app.images.map((img, idx) => (
                      <div key={idx} className="min-w-[45%] aspect-[9/18] rounded-2xl overflow-hidden border border-white/10 shadow-2xl transition-transform duration-500 snap-center">
                        <img src={img} alt={`${app.title} screenshot ${idx}`} className="w-full h-full object-cover" />
                      </div>
                    ))}
                  </div>
                  {/* Slider indicator line */}
                  <div className="mt-2 flex justify-center">
                    <div className="h-[3px] w-24 bg-slate-800 rounded-full overflow-hidden">
                      <div className="h-full w-1/3 bg-ai-glow/60 rounded-full" />
                    </div>
                  </div>
                  <p className="text-[10px] text-center text-slate-500 mt-2">Scroll to view more screens</p>
                </div>

                {/* Badge */}
                <div className="mb-4">
                  <span className="inline-block px-4 py-1.5 rounded-full bg-ai-glow/10 border border-ai-glow/20 text-ai-glow text-[10px] font-bold tracking-widest uppercase">
                    Android Application
                  </span>
                </div>

                {/* Content */}
                <h3 className="text-xl font-bold text-white mb-5 group-hover:text-ai-glow transition-colors">
                  {app.title}
                </h3>
                
                <ul className="space-y-4 mb-8 flex-grow">
                  {app.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <div className="mt-2 w-1.5 h-1.5 rounded-full bg-ai-glow shrink-0 shadow-[0_0_8px_rgba(34,211,238,0.8)]" />
                      <p className="text-sm text-slate-300 leading-relaxed font-medium">
                        {feature}
                      </p>
                    </li>
                  ))}
                </ul>

                {/* Download Button */}
                <a
                  href={app.apkUrl}
                  className="mt-auto inline-flex items-center justify-center gap-2 w-full py-4 rounded-xl bg-ai-glow text-primary-dark font-bold text-sm transition-all hover:scale-[1.02] active:scale-[0.98] shadow-[0_10px_20px_rgba(34,211,238,0.2)]"
                >
                  <Download className="w-4 h-4" />
                  Download APK
                </a>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.5 }}>
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <input
                type="text"
                placeholder="Search projects..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-11 pr-4 py-3 rounded-xl bg-surface/50 text-white placeholder-slate-500 border border-accent/10 focus:border-accent/30 focus:outline-none text-sm"
              />
            </div>
          </div>
          <div className="flex flex-wrap gap-2 mb-4">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveFilter(cat)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer border-none ${
                  activeFilter === cat
                    ? "bg-accent text-white shadow-lg shadow-accent/20"
                    : "bg-surface/30 text-slate-400 hover:bg-surface/50 hover:text-white"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
          <p className="text-sm text-slate-500">Showing {filtered.length} projects</p>
        </motion.div>
      </section>

      {/* Project Cards */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((project, i) => (
            <motion.div
              key={project.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: Math.min(i * 0.05, 0.5), duration: 0.4 }}
              whileHover={{ y: -4 }}
              className="rounded-2xl p-6 group cursor-pointer"
              style={{ background: "rgba(10, 16, 41, 0.4)", backdropFilter: "blur(12px)", border: "1px solid rgba(124, 77, 255, 0.1)" }}
            >
              <span className="inline-block px-3 py-1 text-xs font-semibold rounded-full bg-accent/15 text-accent-light border border-accent/20 mb-3">
                {project.category}
              </span>
              <h3 className="text-lg font-bold text-white mb-2 group-hover:text-ai-glow transition-colors">{project.title}</h3>
              <p className="text-sm text-slate-400 leading-relaxed mb-4">{project.desc}</p>
              <p className="text-xs text-slate-500 font-mono">{project.tech}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Metrics */}
      <section className="section-padding">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Success <span className="gradient-text">Metrics</span>
            </h2>
          </motion.div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-6">
            {metrics.map((m, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1, duration: 0.4 }}
                className="rounded-2xl p-5 text-center"
                style={{ background: "rgba(10, 16, 41, 0.4)", border: "1px solid rgba(124, 77, 255, 0.1)" }}
              >
                <div className="text-2xl font-bold gradient-text mb-1">{m.value}</div>
                <div className="text-xs text-slate-400">{m.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="section-padding">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              What Our Clients <span className="gradient-text">Say</span>
            </h2>
          </motion.div>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              { text: "The AI document processing system reduced our manual review time by 80% and improved accuracy significantly.", author: "Rajesh Kumar", role: "CTO, National Banking Group" },
              { text: "Their predictive maintenance solution saved us millions in unplanned downtime. The AI system identified issues we didn't even know we had.", author: "Priya Sharma", role: "Operations Director, Manufacturing Corp" },
            ].map((t, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.15, duration: 0.5 }}
                className="rounded-2xl p-8"
                style={{ background: "rgba(10, 16, 41, 0.4)", border: "1px solid rgba(124, 77, 255, 0.1)" }}
              >
                <p className="text-slate-300 italic leading-relaxed mb-6">"{t.text}"</p>
                <div>
                  <div className="text-white font-semibold">{t.author}</div>
                  <div className="text-sm text-slate-500">{t.role}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {selectedAndroidApp && (
        <div className="fixed inset-0 z-[80] bg-black/80 backdrop-blur-sm p-4 flex items-center justify-center" onClick={() => setSelectedAndroidApp(null)}>
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.25 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-5xl max-h-[90vh] overflow-y-auto rounded-2xl p-6"
            style={{ background: "rgba(6, 11, 31, 0.95)", border: "1px solid rgba(124, 77, 255, 0.2)" }}
          >
            <div className="flex items-start justify-between gap-4 mb-3">
              <div>
                <h3 className="text-2xl font-bold text-white mb-1">{selectedAndroidApp.title}</h3>
                <p className="text-slate-400 text-sm">All screenshots are shown below.</p>
              </div>
              <button
                type="button"
                onClick={() => setSelectedAndroidApp(null)}
                className="w-9 h-9 rounded-lg bg-slate-800/80 hover:bg-slate-700/80 transition-colors flex items-center justify-center"
                aria-label="Close preview"
              >
                <X className="w-5 h-5 text-slate-300" />
              </button>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
              {selectedAndroidApp.images.map((img, idx) => (
                <div
                  key={`${selectedAndroidApp.title}-${idx}`}
                  className="rounded-xl overflow-hidden border border-slate-700/50"
                >
                  <img
                    src={img}
                    alt={`${selectedAndroidApp.title} screen ${idx + 1}`}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
              ))}
            </div>

            <div className="flex justify-end">
              {selectedAndroidApp.apkUrl ? (
                <a
                  href={selectedAndroidApp.apkUrl}
                  download
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-accent hover:bg-accent/90 text-white text-sm font-medium transition-colors"
                >
                  <Download className="w-4 h-4" />
                  Download APK
                </a>
              ) : (
                <button
                  type="button"
                  disabled
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-slate-700 text-slate-300 text-sm font-medium cursor-not-allowed"
                >
                  <Download className="w-4 h-4" />
                  APK Not Available
                </button>
              )}
            </div>
          </motion.div>
        </div>
      )}

      <FooterNew />
    </main>
  );
};

export default Projects;
